import { parseWatchlistPublicationReceipt } from './watchlistPublication';
import { normalizeWatchlistLifecycle } from './watchlistLifecycle';

export const WATCHLIST_REVISION_PROGRESS_EVENT = 'alphamizer:watchlist-revision-progress';
export const WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT = 'alphamizer:watchlist-revision-progress-observed';
export const MSI_SECURITY_RESOLUTION_APPLIED_EVENT = 'alphamizer:msi-security-resolution-applied';
export const WATCHLIST_HISTORY_OPEN_EVENT = 'alphamizer:watchlist-history-open';
// A pending Watchlist change was ignored. An open Watchlist History reloads,
// so the entry stops showing as pending.
export const WATCHLIST_REVISION_CLOSED_EVENT = 'alphamizer:watchlist-revision-closed';
export const USER_SETTINGS_OPEN_EVENT = 'alphamizer:user-settings-open';
export const WATCHLIST_M365_CONNECTION_CHANGED_EVENT = (
  'alphamizer:watchlist-m365-connection-changed'
);
export const SECURITY_OPERATION_SESSION_SCOPE_FIELD = 'session_scope_token';
export const WATCHLIST_PUBLICATION_VERIFICATION_MESSAGE = (
  'Alphamizer is finishing your Watchlist update and will retry automatically. '
  + 'No action is needed.'
);
export const WATCHLIST_PUBLICATION_CONTRACT_ERROR_CODE = (
  'watchlist_publication_contract_invalid'
);
const WATCHLIST_REVISION_RECEIPT_UNAVAILABLE_CODE = (
  'watchlist_revision_receipt_unavailable'
);
const WATCHLIST_RECEIPT_ERROR_PATTERN = (
  /(?:publication|revision(?:[-\s]+sequence)?)\s+receipt/i
);

function watchlistErrorDetailMessage(errorLike) {
  const detail = errorLike?.detail;
  if (detail && typeof detail === 'object') {
    return String(detail.message || detail.detail || '').trim();
  }
  return detail == null ? '' : String(detail).trim();
}

/**
 * Keep publication-contract diagnostics in engineering logs while exposing one
 * retrying message at every Watchlist Apply/status UI boundary.
 */
export const classifyWatchlistPublicationError = (
  errorLike,
  fallbackMessage = 'Watchlist update status is temporarily unavailable.',
) => {
  const detail = errorLike?.detail;
  const code = String(
    errorLike?.code
    || (detail && typeof detail === 'object' ? detail.code : '')
    || '',
  ).trim();
  const technicalMessage = String(
    (errorLike instanceof Error ? errorLike.message : errorLike?.message)
    || watchlistErrorDetailMessage(errorLike)
    || errorLike
    || fallbackMessage,
  ).trim();
  const searchableDetail = [
    technicalMessage,
    watchlistErrorDetailMessage(errorLike),
  ].filter(Boolean).join(' ');
  const classificationContractError = /watchlist delta classification/i.test(
    searchableDetail,
  );
  const verificationPending = (
    code === WATCHLIST_REVISION_RECEIPT_UNAVAILABLE_CODE
    || code === WATCHLIST_PUBLICATION_CONTRACT_ERROR_CODE
    || classificationContractError
    || WATCHLIST_RECEIPT_ERROR_PATTERN.test(searchableDetail)
  );

  if (verificationPending) {
    if (classificationContractError) {
      console.error(
        'Watchlist terminal publication classification contract error:',
        technicalMessage,
      );
    } else {
      console.error(
        'Watchlist publication verification contract error:',
        { code: code || null, detail: detail || technicalMessage },
      );
    }
  }

  return {
    code: code || null,
    message: verificationPending
      ? WATCHLIST_PUBLICATION_VERIFICATION_MESSAGE
      : technicalMessage,
    technicalMessage,
    verificationPending,
  };
};

export const WATCHLIST_DELTA_CLASSIFICATIONS = Object.freeze({
  MEMBERSHIP_CHANGED: 'membership_changed',
  CATEGORIZATION_CHANGED: 'categorization_changed',
  CUSTOM_METADATA_CHANGED: 'custom_metadata_changed',
  NO_CHANGE: 'no_change',
});

// A successor revision can be metadata-only relative to its immediate parent
// while still superseding a membership revision that the client has not yet
// materialized. Keep that client obligation separate from the backend-owned
// per-revision delta classification.
export const WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD =
  'watchlist_client_publication_required';
export const WATCHLIST_CLIENT_ACTUAL_SYNC_APPLIED_FIELD =
  'watchlist_client_actual_sync_applied';

export const requiresWatchlistClientPublication = (detail) => (
  detail?.[WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD] === true
);

export const canonicalWatchlistPublicationObservation = (detail) => {
  if (!detail || typeof detail !== 'object') return null;
  return { ...detail };
};

const SUPPORTED_WATCHLIST_DELTA_CLASSIFICATIONS = new Set(
  Object.values(WATCHLIST_DELTA_CLASSIFICATIONS),
);

/**
 * Parse the backend-owned mutation classification without inferring membership
 * from counts, status, operation type, or other compatibility flags.
 */
export const parseWatchlistDeltaClassification = (detail) => {
  if (!detail || typeof detail !== 'object') {
    return { classification: null, error: 'Missing watchlist delta classification.' };
  }
  const candidates = [
    detail.delta_classification,
    detail.watchlist_mutation_delta?.delta_classification,
    detail.lifecycle?.terminal_receipt?.delta_classification,
    detail.terminal_receipt?.delta_classification,
  ]
    .filter((value) => value != null)
    .map((value) => String(value).trim().toLowerCase())
    .filter(Boolean);
  const uniqueCandidates = [...new Set(candidates)];
  if (uniqueCandidates.length === 0) {
    return { classification: null, error: 'Missing watchlist delta classification.' };
  }
  if (uniqueCandidates.length !== 1) {
    return { classification: null, error: 'Conflicting watchlist delta classifications.' };
  }
  const classification = uniqueCandidates[0];
  if (!SUPPORTED_WATCHLIST_DELTA_CLASSIFICATIONS.has(classification)) {
    return {
      classification: null,
      error: `Unsupported watchlist delta classification: ${classification}.`,
    };
  }
  return { classification, error: null };
};

export const normalizeSecurityOperationSessionScope = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

export const scopeSecurityOperationEventDetail = (detail, sessionScopeToken) => {
  const normalizedScope = normalizeSecurityOperationSessionScope(sessionScopeToken);
  if (!normalizedScope || !detail || typeof detail !== 'object') return null;
  return {
    ...detail,
    [SECURITY_OPERATION_SESSION_SCOPE_FIELD]: normalizedScope,
  };
};

export const dispatchMsiSecurityResolutionAppliedEvent = (
  detail,
  sessionScopeToken,
) => {
  if (typeof window === 'undefined') return false;
  const scopedDetail = scopeSecurityOperationEventDetail(detail, sessionScopeToken);
  if (!scopedDetail) return false;
  const lifecycle = normalizeWatchlistLifecycle(scopedDetail);
  if (!['succeeded', 'unchanged'].includes(lifecycle.status)) return false;
  window.dispatchEvent(new CustomEvent(MSI_SECURITY_RESOLUTION_APPLIED_EVENT, {
    detail: scopedDetail,
  }));
  if (
    parseWatchlistDeltaClassification(scopedDetail).classification
      === WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
    || requiresWatchlistClientPublication(scopedDetail)
  ) {
    window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT, {
      detail: canonicalWatchlistPublicationObservation(scopedDetail),
    }));
  }
  return true;
};

export const dispatchWatchlistRevisionClosedEvent = (revisionId, sessionScopeToken) => {
  const normalizedRevisionId = String(revisionId || '').trim();
  if (typeof window === 'undefined' || !normalizedRevisionId) return;
  const detail = scopeSecurityOperationEventDetail(
    { watchlist_revision_id: normalizedRevisionId },
    sessionScopeToken,
  );
  if (!detail) return;
  window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_CLOSED_EVENT, { detail }));
};

export const isSecurityOperationEventForScope = (detail, sessionScopeToken) => {
  const expectedScope = normalizeSecurityOperationSessionScope(sessionScopeToken);
  return Boolean(
    expectedScope
    && detail
    && typeof detail === 'object'
    && normalizeSecurityOperationSessionScope(
      detail[SECURITY_OPERATION_SESSION_SCOPE_FIELD],
    ) === expectedScope
  );
};

export const isDuplicateOfCurrentWatchlistRevision = (detail) => {
  if (!detail || typeof detail !== 'object') {
    return false;
  }
  const candidates = [
    detail,
    detail.source_metadata_json,
    detail.pending_revision,
    detail.pending_revision?.source_metadata_json,
    detail.pending_review,
    detail.pending_review?.source_metadata_json,
    detail.staged_review,
    detail.staged_review?.source_metadata_json,
  ];
  return candidates.some((candidate) => candidate?.duplicate_of_current === true);
};

export const isTerminalWatchlistRevisionProgress = (detail) => {
  try {
    return normalizeWatchlistLifecycle(detail).terminal_receipt != null;
  } catch (_error) {
    return false;
  }
};

export const watchlistPublicationKey = (value) => {
  if (!value || typeof value !== 'object') {
    return '';
  }
  const revisionId = String(
    value.watchlist_revision_id
    || '',
  ).trim();
  if (revisionId) {
    return `revision:${revisionId}`;
  }
  return '';
};

export const isSuccessfulWatchlistRevisionPublication = (detail) => {
  // A durable backend pointer move is necessary but is not yet a complete
  // browser publication. Callers that explicitly mark the client commit as
  // pending must not replace their settled read models until the final event.
  if (
    detail?.ui_commit_complete !== true
  ) {
    return false;
  }
  if (!parseWatchlistPublicationReceipt(detail)) {
    return false;
  }
  if (!isTerminalWatchlistRevisionProgress(detail)) {
    return false;
  }
  let status;
  try {
    status = normalizeWatchlistLifecycle(detail).status;
  } catch (_error) {
    return false;
  }
  if (['cancelled', 'failed', 'stale', 'superseded'].includes(status)) {
    return false;
  }
  return ['succeeded', 'unchanged'].includes(status);
};
